// Menú lateral de la plataforma nueva (vidrio fuerte, flotante sobre la aurora).
//   escritorio: fijo a la izquierda, 288 px; colapsado = riel de íconos (con tooltip y nombre accesible). El botón
//               para contraerlo/expandirlo va junto al logo (no hay barra superior).
//   móvil (< 768 px): cajón encima de la página, con velo; Esc o tocar el velo lo cierran.
// Contenido: logo (+ co-branding por dominio), módulos del usuario en el orden de la de siempre (los apagados se
// ven deshabilitados con «Este módulo no está disponible para tu cuenta») y la tarjeta del usuario con su menú.
// Como los acordeones «AI Pricing Engine» y «Analytics Dashboards» de siempre (sidebarmenu.jsx:980-1362), la
// NavSecundaria de un módulo se despliega en acordeón JUSTO DEBAJO de su opción: la del módulo activo abierta, y la
// flecha junto a cada opción la abre desde cualquier módulo (desde Alertas se elige una consulta guardada, se abre una
// conversación o se salta a una página de PowerBI sin entrar antes).
// Movimiento (skill: momentos de marca + liquid-nav): el logo se arma (DbLogoMotion) cada vez que el menú se abre
// y el isotipo entra con resorte cuando se contrae; al pasar el puntero, una pastilla de vidrio se desliza con resorte
// detrás de la opción y un brillo sigue al cursor. Con «reducir movimiento» todo queda quieto.
// CONTRATO: <nav aria-label="Módulos"> con un enlace por módulo cuyo nombre es el título del módulo; botones
// «Contraer menú» / «Expandir menú» (escritorio) y «Mostrar/Ocultar el menú de …» (acordeones).
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ChevronDown, ChevronUp, Lock, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { Glass } from '../../ds/components/core/Glass';
import { IconButton } from '../../ds/components/core/Button';
import { Tooltip } from '../../ds/components/overlays/Tooltip';
import { Skeleton } from '../../ds/components/feedback/Skeleton';
import { DbLogo } from '../../ds/brand/DbLogo';
import { DbLogoMotion } from '../../ds/brand/DbLogoMotion';
import { useDbTheme } from '../../ds/theme/ThemeProvider';
import { cn } from '../../ds/lib/cn';
import { useLanguage } from '../../i18n';
import { sidebarCoBrandFor } from '../../config/branding';
import { MODULOS } from '../modulos/registro';
import { moduloApagado, tieneModulo, useConfigUsuario } from '../app/ConfigUsuario';
import { useReportesPowerBI } from '../comun/reportesPowerBI';
import { LimiteDeError } from '../comun/LimiteDeError';
import { useNavegacion } from './navegacion';
import MenuUsuario from './MenuUsuario';

const FILA = 'db-menu-fila group flex min-h-[44px] w-full items-center gap-3 rounded-md px-3 text-body-sm font-semibold transition-colors duration-fast';
const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

// Los gestos de los íconos (AnimatedIcon, librería motion: ~125 KB) no van en el paquete principal: el menú pinta
// el ícono quieto y el gesto llega en cuanto se descarga su chunk (no se nota: sólo se anima al pasar el puntero).
// Si el chunk no llega (pestaña abierta durante un deploy), se queda el ícono quieto: el menú nunca se cae por un gesto.
function IconoSinGesto({ icon: Icono, size }) {
  return <Icono size={size} strokeWidth={2} aria-hidden="true" />;
}
const AnimatedIcon = lazy(() => import('../../ds/components/motion/AnimatedIcon')
  .then((m) => ({ default: m.AnimatedIcon }))
  .catch(() => ({ default: IconoSinGesto })));

function IconoModulo({ icono: Icono, gesto, activo }) {
  const quieto = <Icono size={18} strokeWidth={2} aria-hidden="true" />;
  return (
    <Suspense fallback={quieto}>
      <AnimatedIcon icon={Icono} gesture={gesto || 'pop'} size={18} active={activo} />
    </Suspense>
  );
}

function Etiqueta({ compacto, children }) {
  return <span className={cn('min-w-0 flex-1 truncate', compacto && 'sr-only')}>{children}</span>;
}

function ConTooltip({ activo, texto, children }) {
  if (!activo) return children;
  return <Tooltip content={texto} placement="right" describe={false} className="w-full">{children}</Tooltip>;
}

// El brillo que sigue al cursor (liquid-nav): la fila guarda la posición del puntero en --mx/--my.
function seguirCursor(e) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
}

function EntradaModulo({ modulo, titulo, actual, apagado, externo, compacto, onNavegar }) {
  const { t } = useLanguage();
  const Icono = modulo.icono;
  const icono = <IconoModulo icono={Icono} gesto={modulo.gesto} activo={actual} />;
  if (apagado) {
    const motivo = t('menu.moduleDisabled');
    return (
      <Tooltip content={motivo} placement="right" className="w-full">
        <button type="button" aria-disabled="true" className={cn(FILA, FOCO, 'cursor-not-allowed text-ink-3', compacto && 'justify-center px-0')}>
          <Icono size={18} aria-hidden="true" />
          <Etiqueta compacto={compacto}>{titulo}</Etiqueta>
          {!compacto && <Lock size={14} aria-hidden="true" className="shrink-0" />}
          <span className="sr-only">. {motivo}</span>
        </button>
      </Tooltip>
    );
  }
  const clases = (activoRuta) => cn(
    FILA, FOCO,
    compacto && 'justify-center px-0',
    activoRuta || actual ? 'db-relief text-accent-ink' : 'text-ink-2 hover:text-ink',
  );
  if (externo) {
    return (
      <ConTooltip activo={compacto} texto={titulo}>
        <a href={externo} className={clases(false)} onClick={onNavegar} onPointerMove={seguirCursor}>
          {icono}
          <Etiqueta compacto={compacto}>{titulo}</Etiqueta>
        </a>
      </ConTooltip>
    );
  }
  const ruta = modulo.rutas[0].ruta;
  // Al acercarse al enlace se descarga el chunk del módulo (registro.js → perezoso): el clic ya no espera la red.
  const precargar = () => {
    modulo.rutas[0].Pagina.precargar?.().catch(() => {});
    modulo.NavSecundaria?.precargar?.().catch(() => {});
  };
  return (
    <ConTooltip activo={compacto} texto={titulo}>
      <NavLink
        to={ruta}
        end={ruta === '/'}
        onClick={onNavegar}
        onPointerEnter={precargar}
        onPointerMove={seguirCursor}
        onFocus={precargar}
        className={({ isActive }) => clases(isActive)}
      >
        {icono}
        <Etiqueta compacto={compacto}>{titulo}</Etiqueta>
      </NavLink>
    </ConTooltip>
  );
}

// Pastilla de vidrio detrás de la opción bajo el puntero (UN elemento absoluto que se desliza con transform).
// Sólo con puntero fino: en táctil no hay «hover». La primera aparición es en su lugar (sin viajar desde arriba).
function usePastilla(lista) {
  const [pastilla, setPastilla] = useState({ y: 0, h: 44, visible: false, salta: true });
  const mover = (e) => {
    if (e.pointerType === 'touch' || !lista.current) return;
    const fila = e.currentTarget;
    const y = fila.getBoundingClientRect().top - lista.current.getBoundingClientRect().top;
    setPastilla((p) => ({ y, h: fila.offsetHeight, visible: true, salta: !p.visible }));
  };
  const ocultar = () => setPastilla((p) => (p.visible ? { ...p, visible: false } : p));
  return { pastilla, mover, ocultar };
}

// Logo de la cabecera: expandido = logotipo animado (se arma al abrir el menú); contraído = isotipo que entra con
// resorte. La primera vez respeta la bienvenida «una vez por sesión» de la skill; después, cada abrir/cerrar anima.
function LogoMenu({ compacto, esMovil, abierto, variante }) {
  const [vueltas, setVueltas] = useState(0);
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) {
      primera.current = false;
      return;
    }
    if (esMovil && !abierto) return; // al cerrar el cajón ya no se ve el logo
    setVueltas((n) => n + 1);
  }, [compacto, abierto, esMovil]);
  if (compacto) {
    return (
      <DbLogo
        key={`iso-${vueltas}`}
        variant={variante}
        lockup="iso"
        aria-hidden="true"
        className={cn('h-9 w-auto text-ink', vueltas > 0 && 'db-logo-pop')}
      />
    );
  }
  return (
    <DbLogoMotion
      key={`logo-${vueltas}`}
      height={72}
      variant={variante}
      introOncePerSession={vueltas === 0}
      basePath="/brand/logo-motion/"
    />
  );
}

export default function MenuLateral({ activo, compacto, esMovil, abierto, onCerrar, onAlternar, onAbrirMfa, panelRef }) {
  const { features, featuresListas, username } = useConfigUsuario();
  const { t } = useLanguage();
  const { theme } = useDbTheme();
  const { alNavegar } = useNavegacion();
  const coBrand = sidebarCoBrandFor(username);
  const dashboardsVisible = tieneModulo(features, { feature: 'powerbiReports' });
  const { reportes } = useReportesPowerBI({ activo: dashboardsVisible });
  const lista = useRef(null);
  const { pastilla, mover, ocultar } = usePastilla(lista);

  const entradas = useMemo(() => MODULOS
    .filter((m) => m.enMenu !== false)
    .map((m) => {
      const apagado = moduloApagado(features, m);
      if (!apagado && !tieneModulo(features, m)) return null;
      // Analytics Dashboards sólo con reportes (o apagado), como la de siempre.
      if (!apagado && m.ocultarSinReportes && (!reportes || reportes.length === 0)) return null;
      const externo = m.externo?.usuarios.includes(username) ? m.externo.url : null;
      return { m, apagado, externo };
    })
    .filter(Boolean), [features, reportes, username]);

  // Acordeón abierto: el del módulo activo (reportes, guía y geo muestran el del chat: su NavSecundaria es la misma),
  // salvo que la persona abra otro con su flecha o lo cierre. Al cambiar de módulo vuelve al del módulo activo.
  const navDe = (m) => (m?.NavSecundaria && tieneModulo(features, { feature: m.featureNav ?? m.feature, optIn: m.optIn })
    ? m.NavSecundaria : null);
  const navActiva = navDe(activo);
  const duenoActivo = navActiva ? entradas.find(({ m, apagado }) => !apagado && navDe(m) === navActiva)?.m.clave ?? null : null;
  const [elegida, setElegida] = useState(null); // null = la del activo · clave · 'ninguna'
  useEffect(() => { setElegida(null); }, [activo?.clave]);
  const abierta = compacto || elegida === 'ninguna' ? null : (elegida ?? duenoActivo);
  const alternarNav = (m) => setElegida(abierta === m.clave ? 'ninguna' : m.clave);
  const cerradoEnMovil = esMovil && !abierto;
  const variante = theme === 'dark' ? 'mono' : 'color';

  return (
    <Glass
      as="nav"
      ref={panelRef}
      id="menu-modulos"
      aria-label="Módulos"
      strength="strong"
      padding="none"
      {...(cerradoEnMovil ? { inert: '' } : {})}
      className={cn(
        'flex flex-col gap-2 overflow-hidden p-2',
        esMovil
          ? 'fixed inset-y-2 left-2 z-overlay w-72 max-w-[calc(100vw-1rem)] transition-transform duration-base ease-out-ios motion-reduce:transition-none'
          : 'relative my-2 ml-2 shrink-0',
        esMovil && (abierto ? 'translate-x-0' : '-translate-x-[110%]'),
        !esMovil && (compacto ? 'w-20' : 'w-72'),
      )}
    >
      {/* El botón conserva su lugar en el árbol al contraer/expandir: el foco se queda en él. */}
      <div className={cn('flex shrink-0 gap-2 px-1 pt-1', compacto ? 'flex-col items-center' : 'items-center justify-between')}>
        <Link
          to="/"
          onClick={alNavegar}
          aria-label="Data Bunker: inicio"
          className={cn('flex min-w-0 items-center gap-3 rounded-md', compacto && 'py-1', FOCO)}
        >
          <LogoMenu compacto={compacto} esMovil={esMovil} abierto={abierto} variante={variante} />
          {coBrand && !compacto && (
            <>
              <span className="h-8 w-px shrink-0 bg-hair-2" aria-hidden="true" />
              <img src={coBrand.logo.src} alt={coBrand.logo.alt} height={32} className="h-8 w-auto min-w-0 object-contain" />
            </>
          )}
        </Link>
        {esMovil ? (
          <IconButton icon={X} label="Cerrar menú" variant="ghost" size="sm" onClick={onCerrar} />
        ) : (
          <IconButton
            icon={compacto ? PanelLeftOpen : PanelLeftClose}
            label={compacto ? 'Expandir menú' : 'Contraer menú'}
            variant="ghost"
            size="sm"
            aria-expanded={!compacto}
            aria-controls="menu-modulos"
            onClick={onAlternar}
          />
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* Sin features todavía (primera vez, sin caché): esqueleto, no módulos adivinados que luego desaparecen. */}
        {!featuresListas && (
          <div role="status" aria-busy="true" aria-label="Cargando módulos" className="flex flex-col gap-2 px-3 py-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="flex h-9 items-center gap-3">
                <Skeleton className="h-5 w-5" radius="sm" />
                {!compacto && <Skeleton className="h-3.5 w-36" />}
              </span>
            ))}
          </div>
        )}
        <ul ref={lista} onPointerLeave={ocultar} className="relative flex flex-col gap-0.5">
          <li
            aria-hidden="true"
            data-quieta={pastilla.salta ? '' : undefined}
            className="db-menu-pastilla pointer-events-none absolute inset-x-0 top-0 rounded-md"
            style={{ transform: `translateY(${pastilla.y}px)`, height: pastilla.h, opacity: pastilla.visible ? 1 : 0 }}
          />
          {featuresListas && entradas.map(({ m, apagado, externo }) => {
            const titulo = m.tituloI18n ? t(m.tituloI18n) : m.titulo;
            const NavSecundaria = !compacto && !apagado && !externo ? navDe(m) : null;
            const navAbierta = Boolean(NavSecundaria) && abierta === m.clave;
            const idNav = `nav-${m.clave}`;
            return (
              <li key={m.clave} className="flex flex-col">
                <div className="relative flex items-center gap-1" onPointerEnter={mover}>
                  <div className="min-w-0 flex-1">
                    <EntradaModulo
                      modulo={m}
                      titulo={titulo}
                      actual={activo?.clave === m.clave}
                      apagado={apagado}
                      externo={externo}
                      compacto={compacto}
                      onNavegar={alNavegar}
                    />
                  </div>
                  {NavSecundaria && (
                    <IconButton
                      icon={navAbierta ? ChevronUp : ChevronDown}
                      label={`${navAbierta ? 'Ocultar' : 'Mostrar'} el menú de ${titulo}`}
                      aria-expanded={navAbierta}
                      aria-controls={idNav}
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onPointerEnter={() => m.NavSecundaria.precargar?.().catch(() => {})}
                      onClick={() => alternarNav(m)}
                    />
                  )}
                </div>
                {navAbierta && (
                  <div id={idNav} role="group" aria-label={`Menú de ${titulo}`} className="db-acordeon relative mb-1 ml-4 mt-0.5 border-l border-hair pl-2">
                    <LimiteDeError modulo="app_root" variante="linea">
                      <Suspense fallback={<Skeleton className="my-2 h-3.5 w-40" />}><NavSecundaria /></Suspense>
                    </LimiteDeError>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="shrink-0 border-t border-hair pt-2">
        <MenuUsuario compacto={compacto} onAbrirMfa={onAbrirMfa} />
      </div>
    </Glass>
  );
}
